import { FormLayout } from "../../model/form/types/FormLayout.enum";
import { Direction } from "../../model/shared/Direction.enum";
import { WidgetKind } from "../../model/widget/types/WidgetKid.enum";

const createIDedSelector = (id: string | number, prefix: string) =>
  `${prefix}${String(id)}`;

export const TestIDs = {
  auth: {
    login: {
      email: "e2e_login-email",
      password: "e2e_login-password",
      submit: "e2e_login-submit",
      twoFactor: {
        submit: "e2e_login-2fa-submit",
        trustDevice: "e2e_login-2fa-trust-device",
        back: "e2e_login-2fa-back",
        methodSwitch: "e2e_login-2fa-method-switch",
      },
    },
    signup: {
      email: "e2e_signup-email",
      name: "e2e_signup-name",
      password: "e2e_signup-password",
      submit: "e2e_signup-submit",
      startOver: "e2e_signup-start-over",
      code: {
        input: "e2e_signup-code-input",
        submit: "e2e_signup-code-submit",
      },
    },
    logout: {
      submit: "e2e_logout-submit",
    },
    languageSwitcher: {
      trigger: "e2e_auth-language-switcher-trigger",
      option: (lang: string) =>
        createIDedSelector(String(lang), "e2e_auth-language-switcher-option-"),
    },
    google: {
      submit: "e2e_google-submit",
    },
  },
  forms: {
    tabs: {
      folders: "e2e_forms_tab_folders",
      forms: "e2e_forms_tab_forms",
    },
    folders: {
      wrapper: (id: string) => createIDedSelector(String(id), "e2e_folder-"),
      delete: {
        button: (id: string) =>
          createIDedSelector(String(id), "e2e_folder-delete-"),
        confirmButton: "e2e_folder-delete-confirm-button",
      },
      edit: (id: string) => createIDedSelector(String(id), "e2e_folder-edit-"),
    },
    forms: {
      wrapper: (id: string) => createIDedSelector(String(id), "e2e_form-"),
      filters: {
        status: {
          dropdown: "e2e_forms-filter-status-dropdown",
          option: (status: string) =>
            createIDedSelector(
              String(status),
              "e2e_forms-filter-status-option-",
            ),
        },
      },
    },
    subHeader: {
      dropdown: {
        open: "e2e_atira-space-form-subheader-menu",
        option: (id: string) =>
          createIDedSelector(
            String(id),
            "e2e_atira-space-form-subheader-menu-option-",
          ),
      },
    },
    createEdit: {
      edit: {
        button: (id: string) =>
          createIDedSelector(String(id), "e2e_forms-edit-"),
      },
      steps: {
        step1: {
          title: {
            input: "e2e_forms-create-edit-step1-title-input",
          },
          domain: {
            input: "e2e_forms-create-edit-step1-domain-input",
            addButton: "e2e_forms-create-edit-step1-domain-add-button",
          },
        },
        step2: {
          inputAdd: {
            field: (name: string) =>
              createIDedSelector(
                name,
                "e2e_forms-create-edit-step2-input-add-",
              ),
            button: "e2e_forms-create-edit-step2-input-add-button",
          },
          inputRemove: (name: string) =>
            createIDedSelector(
              name,
              "e2e_forms-create-edit-step2-input-remove-",
            ),
          inputConfigure: (name: string) =>
            createIDedSelector(
              name,
              "e2e_forms-create-edit-step2-input-configure-",
            ),
          inputConfigureModal: {
            save: (name: string) =>
              createIDedSelector(
                name,
                "e2e_forms-create-edit-step2-input-configure-modal-save-",
              ),
            label: (name: string) =>
              createIDedSelector(
                name,
                "e2e_forms-create-edit-step2-input-configure-modal-label-",
              ),
            placeholder: (name: string) =>
              createIDedSelector(
                name,
                "e2e_forms-create-edit-step2-input-configure-modal-placeholder-",
              ),
            maxChar: (name: string) =>
              createIDedSelector(
                name,
                "e2e_forms-create-edit-step2-input-configure-modal-max-char-",
              ),
            required: (name: string) =>
              createIDedSelector(
                name,
                "e2e_forms-create-edit-step2-input-configure-modal-required-",
              ),
            optionAdd: (name: string) =>
              createIDedSelector(
                name,
                "e2e_forms-create-edit-step2-input-configure-modal-option-add-",
              ),
            option: (name: string, index: number) =>
              createIDedSelector(
                `${name}-${index}`,
                "e2e_forms-create-edit-step2-input-configure-modal-option-",
              ),
            optionDefault: (name: string, index: number) =>
              createIDedSelector(
                `${name}-${index}`,
                "e2e_forms-create-edit-step2-input-configure-modal-option-default-",
              ),
            dateConfig: {
              enabled: (name: string) =>
                createIDedSelector(
                  name,
                  "e2e_forms-create-edit-step2-input-configure-modal-date-enabled-",
                ),
              timezone: (name: string) =>
                createIDedSelector(
                  name,
                  "e2e_forms-create-edit-step2-input-configure-modal-date-timezone-",
                ),
              day: (name: string, day: string) =>
                createIDedSelector(
                  `${name}-${day}`,
                  "e2e_forms-create-edit-step2-input-configure-modal-date-day-",
                ),
              hourStart: (name: string) =>
                createIDedSelector(
                  name,
                  "e2e_forms-create-edit-step2-input-configure-modal-date-hour-start-",
                ),
              hourStartOption: (name: string, hour: string) =>
                createIDedSelector(
                  `${name}-${hour}`,
                  "e2e_forms-create-edit-step2-input-configure-modal-date-hour-start-option-",
                ),
              hourEnd: (name: string) =>
                createIDedSelector(
                  name,
                  "e2e_forms-create-edit-step2-input-configure-modal-date-hour-end-",
                ),
              hourEndOption: (name: string, hour: string) =>
                createIDedSelector(
                  `${name}-${hour}`,
                  "e2e_forms-create-edit-step2-input-configure-modal-date-hour-end-option-",
                ),
              addHour: (name: string) =>
                createIDedSelector(
                  name,
                  "e2e_forms-create-edit-step2-input-configure-modal-date-hour-add-",
                ),
              slotDuration: (name: string) =>
                createIDedSelector(
                  name,
                  "e2e_forms-create-edit-step2-input-configure-modal-date-slot-duration-",
                ),
              slotDurationOption: (name: string, minutes: string) =>
                createIDedSelector(
                  `${name}-${minutes}`,
                  "e2e_forms-create-edit-step2-input-configure-modal-date-slot-duration-option-",
                ),
            },
          },
          landingPageElements: {
            header: {
              open: "e2e_forms-create-edit-step2-header-open",
              save: "e2e_forms-create-edit-step2-header-save-button",
              title: "e2e_forms-create-edit-step2-header-title-input",
              image: "e2e_forms-create-edit-step2-header-image-input",
              contentAlign: {
                open: "e2e_forms-create-edit-step2-header-content-align-open",
                option: (option: string) =>
                  createIDedSelector(
                    String(option),
                    "e2e_forms-create-edit-step2-header-content-align-option-",
                  ),
              },
              contentDirection: {
                open: "e2e_forms-create-edit-step2-header-content-direction-open",
                option: (option: string) =>
                  createIDedSelector(
                    String(option),
                    "e2e_forms-create-edit-step2-header-content-direction-option-",
                  ),
              },
              titleFontSize: {
                open: "e2e_forms-create-edit-step2-header-title-font-size-open",
                option: (option: string) =>
                  createIDedSelector(
                    String(option),
                    "e2e_forms-create-edit-step2-header-title-font-size-option-",
                  ),
              },
              imageBorderRadius: {
                open: "e2e_forms-create-edit-step2-header-image-border-radius-open",
                option: (option: string) =>
                  createIDedSelector(
                    String(option),
                    "e2e_forms-create-edit-step2-header-image-border-radius-option-",
                  ),
              },
            },
            leftSide: {
              open: "e2e_forms-create-edit-step2-left-side-open",
              save: "e2e_forms-create-edit-step2-left-side-save-button",
              header: "e2e_forms-create-edit-step2-left-side-header-input",
              body: "e2e_forms-create-edit-step2-left-side-body-input",
              image: "e2e_forms-create-edit-step2-left-side-image-input",
              contentDirection: {
                open: "e2e_forms-create-edit-step2-left-side-content-direction-open",
                option: (option: string) =>
                  createIDedSelector(
                    String(option),
                    "e2e_forms-create-edit-step2-left-side-content-direction-option-",
                  ),
              },
              contentAlign: {
                open: "e2e_forms-create-edit-step2-left-side-content-align-open",
                option: (option: string) =>
                  createIDedSelector(
                    String(option),
                    "e2e_forms-create-edit-step2-left-side-content-align-option-",
                  ),
              },
              textFontSize: {
                open: "e2e_forms-create-edit-step2-left-side-text-font-size-open",
                option: (option: string) =>
                  createIDedSelector(
                    String(option),
                    "e2e_forms-create-edit-step2-left-side-text-font-size-option-",
                  ),
              },
              headerFontSize: {
                open: "e2e_forms-create-edit-step2-left-side-header-font-size-open",
                option: (option: string) =>
                  createIDedSelector(
                    String(option),
                    "e2e_forms-create-edit-step2-left-side-header-font-size-option-",
                  ),
              },
              imageBorderRadius: {
                open: "e2e_forms-create-edit-step2-left-side-image-border-radius-open",
                option: (option: string) =>
                  createIDedSelector(
                    String(option),
                    "e2e_forms-create-edit-step2-left-side-image-border-radius-option-",
                  ),
              },
            },
            footer: {
              open: "e2e_forms-create-edit-step2-footer-open",
              save: "e2e_forms-create-edit-step2-footer-save-button",
              text: "e2e_forms-create-edit-step2-footer-text-input",
              textAlign: {
                open: "e2e_forms-create-edit-step2-footer-text-align-open",
                option: (option: string) =>
                  createIDedSelector(
                    String(option),
                    "e2e_forms-create-edit-step2-footer-text-align-option-",
                  ),
              },
              textFontSize: {
                open: "e2e_forms-create-edit-step2-footer-text-font-size-open",
                option: (option: string) =>
                  createIDedSelector(
                    String(option),
                    "e2e_forms-create-edit-step2-footer-text-font-size-option-",
                  ),
              },
            },
          },
        },
        step3: {
          postSubmitType: {
            radio: {
              confirm: "e2e_forms-create-edit-step3-post-submit-radio-confirm",
              redirect:
                "e2e_forms-create-edit-step3-post-submit-radio-redirect",
            },
            confirm: {
              input: "e2e_forms-create-edit-step3-post-submit-confirm-input",
            },
            redirect: {
              input: "e2e_forms-create-edit-step3-post-submit-redirect-input",
            },
          },
        },
        step4: {
          customFields: {
            dropdown: {
              open: (id: string) =>
                createIDedSelector(
                  String(id),
                  "e2e_atira-space-create-custom-fields-dropdown-open-",
                ),
              option: (id: string) =>
                createIDedSelector(
                  String(id),
                  "e2e_atira-space-create-custom-fields-dropdown-option-",
                ),
            },
          },
        },
        step5: {
          theme: {
            dropdown: {
              open: (id: string) =>
                createIDedSelector(
                  String(id),
                  "e2e_forms-create-edit-step5-theme-dropdown-open-",
                ),
              option: (id: string) =>
                createIDedSelector(
                  String(id),
                  "e2e_forms-create-edit-step5-theme-dropdown-option-",
                ),
            },
          },
        },
        step6: {
          tabs: {
            form: "e2e_forms-create-edit-step6-tab-form",
            input: "e2e_forms-create-edit-step6-tab-input",
            button: "e2e_forms-create-edit-step6-tab-button",
            header: "e2e_forms-create-edit-step6-tab-header",
            theme: "e2e_forms-create-edit-step6-tab-theme",
            title: "e2e_forms-create-edit-step6-tab-title",
          },
          formSettings: {
            button: "e2e_forms-create-edit-step6-form-settings-title",
            settings: {
              formDirection: {
                [Direction.LTR]:
                  "e2e_forms-create-edit-step6-form-direction-ltr",
                [Direction.RTL]:
                  "e2e_forms-create-edit-step6-form-direction-rtl",
              },
              border: {
                borderRadius: {
                  open: "e2e_forms-create-edit-step6-form-border-radius-open",
                  option: (option: string) =>
                    createIDedSelector(
                      String(option),
                      "e2e_forms-create-edit-step6-form-border-radius-option-",
                    ),
                },
                borderThickness: {
                  open: "e2e_forms-create-edit-step6-form-border-thickness-open",
                  option: (option: string) =>
                    createIDedSelector(
                      String(option),
                      "e2e_forms-create-edit-step6-form-border-thickness-option-",
                    ),
                },
              },
              innerPadding: {
                input:
                  "e2e_forms-create-edit-step6-padding-inner-padding-input",
              },
              shadow: {
                open: "e2e_forms-create-edit-step6-shadow-open",
                option: (shadow: string) =>
                  createIDedSelector(
                    String(shadow),
                    "e2e_forms-create-edit-step6-shadow-option-",
                  ),
              },
              colors: {
                shadowColor: {
                  picker:
                    "e2e_forms-create-edit-step6-colors-shadow-color-picker",
                },
                containerBackgroundColor: {
                  pickerPrefix:
                    "e2e_forms-create-edit-step6-colors-container-background-color-picker-prefix",
                  picker:
                    "e2e_forms-create-edit-step6-colors-container-background-color-picker",
                },
                formBackgroundColor: {
                  pickerPrefix:
                    "e2e_forms-create-edit-step6-colors-form-background-color-picker-prefix",
                  picker:
                    "e2e_forms-create-edit-step6-colors-form-background-color-picker",
                },
                borderColor: {
                  picker:
                    "e2e_forms-create-edit-step6-form-colors-border-color-picker",
                },
              },
            },
          },
          inputSettings: {
            button: "e2e_forms-create-edit-step6-input-settings-title",
            settings: {
              layout: {
                [FormLayout.HORIZONTAL]:
                  "e2e_forms-create-edit-step6-input-layout-horizontal",
                [FormLayout.VERTICAL]:
                  "e2e_forms-create-edit-step6-input-layout-vertical",
              },
              gap: "e2e_forms-create-edit-step6-inputs-gap",
              verticalGap: "e2e_forms-create-edit-step6-inputs-vertical-gap",
              border: {
                borderRadius: {
                  open: "e2e_forms-create-edit-step6-input-border-radius-open",
                  option: (option: string) =>
                    createIDedSelector(
                      String(option),
                      "e2e_forms-create-edit-step6-input-border-radius-option-",
                    ),
                },
                borderThickness: {
                  open: "e2e_forms-create-edit-step6-input-border-thickness-open",
                  option: (option: string) =>
                    createIDedSelector(
                      String(option),
                      "e2e_forms-create-edit-step6-input-border-thickness-option-",
                    ),
                },
              },
              shadow: {
                borderShadow: {
                  open: "e2e_forms-create-edit-step6-input-border-shadow-open",
                  option: (option: string) =>
                    createIDedSelector(
                      String(option),
                      "e2e_forms-create-edit-step6-input-border-shadow-option-",
                    ),
                },
              },
              colors: {
                backgroundColor: {
                  picker:
                    "e2e_forms-create-edit-step6-input-colors-background-color-picker",
                },
                borderColor: {
                  picker:
                    "e2e_forms-create-edit-step6-input-colors-border-color-picker",
                },
                placeholderColor: {
                  picker:
                    "e2e_forms-create-edit-step6-input-colors-placeholder-color-picker",
                },
                color: {
                  picker:
                    "e2e_forms-create-edit-step6-input-colors-color-picker",
                },
                shadowColor: {
                  picker:
                    "e2e_forms-create-edit-step6-input-colors-shadow-color-picker",
                },
                focusBorderColor: {
                  picker:
                    "e2e_forms-create-edit-step6-colors-focus-border-color-picker",
                },
              },
            },
          },
          headerSettings: {
            button: "e2e_forms-create-edit-step6-header-settings-title",
            settings: {
              switch: "e2e_forms-create-edit-step6-header-settings-switch",
              title: {
                input:
                  "e2e_forms-create-edit-step6-header-settings-title-input",
                titleColor: {
                  picker:
                    "e2e_forms-create-edit-step6-header-settings-title-color-picker",
                },
                headerBackgroundColor: {
                  picker:
                    "e2e_forms-create-edit-step6-header-settings-background-color-picker",
                },
              },
            },
          },
          buttonSettings: {
            button: "e2e_forms-create-edit-step6-button-settings-title",
            settings: {
              buttonSettings: {
                buttonTitle: {
                  input:
                    "e2e_forms-create-edit-step6-button-settings-title-input",
                },
                buttonWidthKind: {
                  select:
                    "e2e_forms-create-edit-step6-button-settings-width-kind-select",
                },
                buttonBorderRadius: {
                  open: "e2e_forms-create-edit-step6-button-settings-border-radius-open",
                  option: (option: string) =>
                    createIDedSelector(
                      String(option),
                      "e2e_forms-create-edit-step6-button-settings-border-radius-option-",
                    ),
                },
                buttonWidth: {
                  input:
                    "e2e_forms-create-edit-step6-button-settings-width-input",
                },
              },
              colors: {
                buttonBackgroundColor: {
                  picker:
                    "e2e_forms-create-edit-step6-button-settings-background-color-picker",
                },
                buttonTextColor: {
                  picker:
                    "e2e_forms-create-edit-step6-button-settings-text-color-picker",
                },
              },
            },
          },
          titleSettings: {
            button: "e2e_forms-create-edit-step6-title-settings-title",
            settings: {
              title: "e2e_forms-create-edit-step6_title-settings-title-input",
              titleColor:
                "e2e_forms-create-edit-step6_title-settings-title-color-picker",
            },
          },
        },
      },
      navigation: {
        nextButton: "e2e_forms-create-edit-navigation-next-button",
        backButton: "e2e_forms-create-edit-navigation-back-button",
      },
    },
    form: {
      actions: {
        menu: (id: string) => createIDedSelector(id, "e2e_form-actions-menu-"),
        delete: (id: string) =>
          createIDedSelector(id, "e2e_form-actions-delete-"),
        deleteModalConfirm: "e2e_form-actions-delete-confirm-button",
        clone: (id: string) =>
          createIDedSelector(id, "e2e_form-actions-clone-"),
        cloneModalConfirm: "e2e_form-actions-clone-confirm-button",
        addToFolder: (id: string) =>
          createIDedSelector(id, "e2e_form-actions-add-to-folder-"),
        removeFromFolder: (id: string) =>
          createIDedSelector(id, "e2e_form-actions-remove-from-folder-"),
        calendar: (id: string) =>
          createIDedSelector(id, "e2e_form-actions-calendar-"),
      },
      addToFolder: {
        modal: {
          dropdown: {
            open: (id: string) =>
              createIDedSelector(
                String(id),
                "e2e_form-add-to-folder-dropdown-open-",
              ),
            option: (folderId: string) =>
              createIDedSelector(
                `${folderId}`,
                "e2e_form-add-to-folder-dropdown-option-",
              ),
          },
          button: "e2e_form-add-to-folder-submit-button",
        },
      },
      removeFromFolder: {
        modal: {
          button: "e2e_form-remove-from-folder-confirm-button",
        },
      },
    },
    calendar: {
      drawer: "e2e_form-calendar-drawer",
      event: (entryId: string, fieldName: string) =>
        createIDedSelector(
          `${entryId}-${fieldName}`,
          "e2e_form-calendar-event-",
        ),
    },
    createEditFolder: {
      modal: {
        name: "e2e_forms-create-edit-folder-modal-name-input",
        description: "e2e_forms-create-edit-folder-modal-description-input",
        icon: "e2e_forms-create-edit-folder-modal-icon-input",
        submit: "e2e_forms-create-edit-folder-modal-submit-button",
      },
    },
  },
  tasks: {
    subHeader: {
      dropdown: {
        open: "e2e_atira-space-task-subheader-menu",
        option: (id: string) =>
          createIDedSelector(
            String(id),
            "e2e_atira-space-task-subheader-menu-option-",
          ),
      },
      kindFilter: {
        open: "e2e_atira-space-task-kind-filter-open",
        option: (value: string) =>
          createIDedSelector(
            String(value),
            "e2e_atira-space-task-kind-filter-option-",
          ),
      },
      search: {
        input: "e2e_atira-space-task-search-input",
      },
    },
    createEdit: {
      drawer: {
        wrapper: "e2e_task-create-edit-drawer",
        button: "e2e_task-create-edit-drawer-submit-button",
        selectContact: "e2e_task-create-edit-select-contact",
        name: "e2e_task-create-edit-name-input",
        reminder: "e2e_task-create-edit-reminder-input",
        deadline: "e2e_task-create-edit-deadline-input",
        description: "e2e_task-create-edit-description-input",
        priority: (priority: string) =>
          createIDedSelector(
            String(priority),
            "e2e_task-create-edit-priority-",
          ),
        rating: "e2e_task-create-edit-rating-input",
      },
    },
    table: {
      row: (id: string) => createIDedSelector(String(id), "e2e_task-row-"),
      actionsCell: (id: string) =>
        createIDedSelector(String(id), "e2e_task-actions-cell-"),
      menu: {
        preview: (id: string) =>
          createIDedSelector(String(id), "e2e_task-preview-button-"),
        done: {
          button: (id: string) =>
            createIDedSelector(String(id), "e2e_task-done-button-"),
          modal: {
            button: "e2e_task-done-confirm-button",
          },
        },
        edit: (id: string) =>
          createIDedSelector(String(id), "e2e_task-edit-button-"),
        delete: {
          button: (id: string) =>
            createIDedSelector(String(id), "e2e_task-delete-button-"),
          modal: {
            button: "e2e_task-delete-confirm-button",
          },
        },
        assign: {
          button: (id: string) =>
            createIDedSelector(String(id), "e2e_task-assign-button-"),
        },
      },
    },
  },
  general: {
    iconSelectModal: {
      wrapper: "e2e_icon-select-modal",
      search: "e2e_icon-select-modal-search",
      tab: (tab: "solid" | "regular") =>
        createIDedSelector(tab, "e2e_icon-select-modal-tab-"),
      icon: (iconName: string) =>
        createIDedSelector(iconName, "e2e_icon-select-modal-icon-"),
      save: "e2e_icon-select-modal-save",
    },
    sidemenu: {
      link: (link: string) =>
        createIDedSelector(String(link), "e2e_sidemenu-link-"),
    },
    subSideMenu: {
      link: (link: string) =>
        createIDedSelector(String(link), "e2e_subsidemenu-link-"),
    },
    header: {
      settings: "e2e_header-settings-button",
    },
    impersonation: {
      banner: "e2e_impersonation-banner",
    },
    settings: {
      language: {
        open: "e2e_settings-language-open",
        option: (lang: string) =>
          createIDedSelector(String(lang), "e2e_settings-language-option-"),
        button: "e2e_settings-language-confirm-button",
      },
      notifications: {
        toggle: (entity: string, notificationCase: string, type: string) =>
          createIDedSelector(
            `${entity}-${notificationCase}-${type}`,
            "e2e_settings-notifications-toggle-",
          ),
        button: "e2e_settings-notifications-confirm-button",
      },
      twoFactor: {
        toggle: "e2e_settings-2fa-toggle",
        password: "e2e_settings-2fa-password",
        passwordSubmit: "e2e_settings-2fa-password-submit",
        code: "e2e_settings-2fa-code",
        codeSubmit: "e2e_settings-2fa-code-submit",
        disableConfirm: "e2e_settings-2fa-disable-confirm",
        totpToggle: "e2e_settings-2fa-totp-toggle",
        totpQr: "e2e_settings-2fa-totp-qr",
        totpSecret: "e2e_settings-2fa-totp-secret",
      },
      tags: {
        create: "e2e_settings-tags-create-button",
        row: (name: string) =>
          createIDedSelector(String(name), "e2e_settings-tags-row-"),
        edit: (name: string) =>
          createIDedSelector(String(name), "e2e_settings-tags-edit-"),
        delete: (name: string) =>
          createIDedSelector(String(name), "e2e_settings-tags-delete-"),
        deleteConfirm: "e2e_settings-tags-delete-confirm-button",
        modal: {
          name: "e2e_settings-tags-modal-name-input",
          color: "e2e_settings-tags-modal-color-button",
          save: "e2e_settings-tags-modal-save-button",
        },
      },
      manageAccounts: {
        add: "e2e_settings-manage-accounts-add-button",
        remove: (userId: string) =>
          createIDedSelector(
            String(userId),
            "e2e_settings-manage-accounts-remove-",
          ),
        switch: (userId: string) =>
          createIDedSelector(
            String(userId),
            "e2e_settings-manage-accounts-switch-",
          ),
        modal: {
          email: {
            email: "e2e_settings-manage-accounts-modal-email-input",
            password: "e2e_settings-manage-accounts-modal-password-input",
            button: "e2e_settings-manage-accounts-modal-confirm-button",
          },
          google: {},
        },
      },
      initialPreferencesModal: {
        open: "e2e_settings_initial_preferences_modal_open",
        mainFields: {
          option: (field: string) =>
            createIDedSelector(
              String(field),
              "e2e_settings_initial_preferences_modal_main_fields_option-",
            ),
        },
        lang: {
          open: "e2e_settings_initial_preferences_modal_lang_open",
          option: (lang: string) =>
            createIDedSelector(
              String(lang),
              "e2e_settings_initial_preferences_modal_lang_option-",
            ),
        },
        timezone: {
          open: "e2e_settings_initial_preferences_modal_timezone_open",
          option: (timezone: string) =>
            createIDedSelector(
              String(timezone),
              "e2e_settings_initial_preferences_modal_timezone_option-",
            ),
        },
        currency: {
          open: "e2e_settings_initial_preferences_modal_currency_open",
          option: (currency: string) =>
            createIDedSelector(
              String(currency),
              "e2e_settings_initial_preferences_modal_currency_option-",
            ),
        },
        save: "e2e_settings_initial_preferences_modal_save_button",
        dismiss: "e2e_settings_initial_preferences_modal_dismiss_button",
      },
    },
  },
  integration: {
    whatsappMeta: {
      section: {
        wrapper: "e2e_integration-whatsapp-meta-section",
        aiAgentButton: "e2e_integration-whatsapp-meta-ai-agent-button",
        viewButton: "e2e_integration-whatsapp-meta-view-button",
        disconnectButton: "e2e_integration-whatsapp-meta-disconnect-button",
        disconnectConfirm: "e2e_integration-whatsapp-meta-disconnect-confirm",
      },
      chat: {
        attach: "e2e_wa-chat-attach",
        record: "e2e_wa-chat-record",
        list: "e2e_wa-chats-list",
        unreadTotal: "e2e_wa-chats-unread-total",
        attentionChip: "e2e_wa-chats-attention-chip",
        headerMenu: "e2e_wa-chats-header-menu",
        createChat: "e2e_wa-chats-create-chat",
        createLead: "e2e_wa-chat-create-lead",
        openLead: "e2e_wa-chat-open-lead",
        emptyThread: "e2e_wa-empty-thread",
        search: {
          open: "e2e_wa-chats-search-open",
          input: "e2e_wa-chats-search-input",
          close: "e2e_wa-chats-search-close",
          empty: "e2e_wa-chats-search-empty",
        },
        tags: {
          button: "e2e_wa-chat-tags-button",
          search: "e2e_wa-chat-tags-search",
          option: (name: string) =>
            createIDedSelector(String(name), "e2e_wa-chat-tags-option-"),
        },
        tagsFilter: {
          button: "e2e_wa-chats-tags-filter-button",
          search: "e2e_wa-chats-tags-filter-search",
          option: (name: string) =>
            createIDedSelector(
              String(name),
              "e2e_wa-chats-tags-filter-option-",
            ),
          clear: "e2e_wa-chats-tags-filter-clear",
        },
        row: (waId: string) =>
          createIDedSelector(String(waId), "e2e_wa-chat-row-"),
        message: (id: string) =>
          createIDedSelector(String(id), "e2e_wa-message-"),
        messageMenu: {
          open: "e2e_wa-message-menu-open",
          delete: "e2e_wa-message-menu-delete",
          confirmDelete: "e2e_wa-message-menu-delete-confirm",
        },
        reaction: (emoji: string) =>
          createIDedSelector(String(emoji), "e2e_wa-reaction-"),
        reactionsModal: {
          wrapper: "e2e_wa-reactions-modal",
          remove: "e2e_wa-reactions-modal-remove",
        },
      },
      campaign: {
        open: "e2e_wa-campaigns-open",
        new: "e2e_wa-campaigns-new",
        card: "e2e_wa-campaign-card",
        edit: "e2e_wa-campaign-edit",
        editor: {
          name: "e2e_wa-campaign-editor-name",
          search: "e2e_wa-campaign-editor-search",
          tags: "e2e_wa-campaign-editor-tags",
          selectAll: "e2e_wa-campaign-editor-select-all",
          unselectAll: "e2e_wa-campaign-editor-unselect-all",
          pick: (waId: string) =>
            createIDedSelector(String(waId), "e2e_wa-campaign-editor-pick-"),
          template: {
            open: "e2e_wa-campaign-editor-template-open",
            option: (id: string) =>
              createIDedSelector(
                String(id),
                "e2e_wa-campaign-editor-template-option-",
              ),
          },
          variable: (key: string) =>
            createIDedSelector(String(key), "e2e_wa-campaign-editor-variable-"),
          useContactName: (key: string) =>
            createIDedSelector(
              String(key),
              "e2e_wa-campaign-editor-use-contact-name-",
            ),
          startLater: "e2e_wa-campaign-editor-start-later",
          startAt: "e2e_wa-campaign-editor-start-at",
          gap: "e2e_wa-campaign-editor-gap",
          gapUnit: {
            open: "e2e_wa-campaign-editor-gap-unit-open",
            option: (unit: string) =>
              createIDedSelector(
                String(unit),
                "e2e_wa-campaign-editor-gap-unit-option-",
              ),
          },
          submit: "e2e_wa-campaign-editor-submit",
        },
      },
      aiAgentDrawer: {
        submit: "e2e_integration-wa-ai-agent-drawer-submit",
        remove: {
          button: "e2e_integration-wa-ai-agent-drawer-remove-button",
          confirm: "e2e_integration-wa-ai-agent-drawer-remove-confirm",
        },
        model: {
          open: "e2e_integration-wa-ai-agent-drawer-model-open",
          provider: (id: string) =>
            createIDedSelector(
              String(id),
              "e2e_integration-wa-ai-agent-drawer-model-provider-",
            ),
          option: (id: string) =>
            createIDedSelector(
              String(id),
              "e2e_integration-wa-ai-agent-drawer-model-option-",
            ),
        },
        fields: {
          enabled: "e2e_integration-wa-ai-agent-drawer-field-enabled",
          name: "e2e_integration-wa-ai-agent-drawer-field-name",
          apiKey: "e2e_integration-wa-ai-agent-drawer-field-api-key",
          prompt: "e2e_integration-wa-ai-agent-drawer-field-prompt",
          dataSource: "e2e_integration-wa-ai-agent-drawer-field-data-source",
          faqQuestion: (index: number) =>
            createIDedSelector(
              String(index),
              "e2e_integration-wa-ai-agent-drawer-field-faq-question-",
            ),
          faqAnswer: (index: number) =>
            createIDedSelector(
              String(index),
              "e2e_integration-wa-ai-agent-drawer-field-faq-answer-",
            ),
          faqButtonAdd: "e2e_integration-wa-ai-agent-drawer-field-faq-add",
          faqButtonRemove: (index: number) =>
            createIDedSelector(
              String(index),
              "e2e_integration-wa-ai-agent-drawer-field-faq-remove-",
            ),
        },
      },
    },
  },
  chatbot: {
    empty: {
      createButton: "e2e_chatbot-empty-create-button",
    },
    list: {
      card: (id: string) => createIDedSelector(String(id), "e2e_chatbot-card-"),
      actions: {
        edit: (id: string) =>
          createIDedSelector(String(id), "e2e_chatbot-edit-button-"),
        delete: (id: string) =>
          createIDedSelector(String(id), "e2e_chatbot-delete-button-"),
        share: (id: string) =>
          createIDedSelector(String(id), "e2e_chatbot-share-button-"),
      },
    },
    drawer: {
      createEdit: {
        navigation: {
          nextButton: "e2e_chatbot-create-drawer-navigation-next-button",
          backButton: "e2e_chatbot-create-drawer-navigation-back-button",
        },
        model: {
          open: "e2e_chatbot-create-drawer-model-open",
          provider: (id: string) =>
            createIDedSelector(
              String(id),
              "e2e_chatbot-create-drawer-model-provider-",
            ),
          option: (id: string) =>
            createIDedSelector(
              String(id),
              "e2e_chatbot-create-drawer-model-option-",
            ),
        },
        form: {
          open: "e2e_chatbot-create-drawer-form-open",
          option: (id: string) =>
            createIDedSelector(
              String(id),
              "e2e_chatbot-create-drawer-form-option-",
            ),
        },
        fields: {
          apiKey: "e2e_chatbot-create-drawer-field-api-key",
          name: "e2e_chatbot-create-drawer-field-name",
          description: "e2e_chatbot-create-drawer-field-description",
          prompt: "e2e_chatbot-create-drawer-field-prompt",
          assistantKind: (kind: string) =>
            createIDedSelector(
              String(kind),
              "e2e_chatbot-create-drawer-field-assistant-kind-",
            ),
          dataSource: "e2e_chatbot-create-drawer-field-data-source",
          requireFormFillingBeforeChatting:
            "e2e_chatbot-create-drawer-field-require-form-filling-before-chatting",
          faqQuestion: (index: number) =>
            createIDedSelector(
              String(index),
              "e2e_chatbot-create-drawer-field-faq-question-",
            ),
          faqAnswer: (index: number) =>
            createIDedSelector(
              String(index),
              "e2e_chatbot-create-drawer-field-faq-answer-",
            ),
          faqButtonAdd: "e2e_chatbot-create-drawer-field-faq-button",
          faqButtonRemove: (index: number) =>
            createIDedSelector(
              String(index),
              "e2e_chatbot-create-drawer-field-faq-remove-button-",
            ),
          color: "e2e_chatbot-create-drawer-field-color",
          position: {
            open: "e2e_chatbot-create-drawer-field-position",
            option: (position: string) =>
              createIDedSelector(
                String(position),
                "e2e_chatbot-create-drawer-field-position-option-",
              ),
          },
          whatsApp: "e2e_chatbot-create-drawer-field-whatsapp",
          whatsAppStarterMessage:
            "e2e_chatbot-create-drawer-field-whatsapp-starter-message",
          leadSummarySummaryField: {
            open: "e2e_chatbot-create-drawer-field-lead-summary-summary-field-open",
            option: (id: string) =>
              createIDedSelector(
                String(id),
                "e2e_chatbot-create-drawer-field-lead-summary-summary-field-option-",
              ),
          },
          textBoxPlaceholder:
            "e2e_chatbot-create-drawer-field-text-box-placeholder",
        },
        submit: "e2e_chatbot-create-drawer-submit",
        cancel: "e2e_chatbot-create-drawer-cancel",
      },
    },
  },
  orgChat: {
    messageList: "e2e_org-chat-message-list",
    message: (id: string) =>
      createIDedSelector(String(id), "e2e_org-chat-message-"),
    input: "e2e_org-chat-input",
    sendButton: "e2e_org-chat-send-button",
    attach: "e2e_org-chat-attach-button",
    record: "e2e_org-chat-record-button",
    templates: "e2e_org-chat-templates-button",
    loadMore: "e2e_org-chat-load-more",
    members: "e2e_org-chat-members",
    member: (id: string) =>
      createIDedSelector(String(id), "e2e_org-chat-member-"),
  },
  entries: {
    createEdit: {
      dropdown: {
        open: "e2e_entries-create-edit-dropdown-open",
        option: (id: string) =>
          createIDedSelector(
            String(id),
            "e2e_entries-create-edit-dropdown-option-",
          ),
      },
      form: {
        fields: {
          field: (name: string) =>
            createIDedSelector(name, "e2e_entries-manual-creation-field-"),
          // AtiraDatePickerFlat derives its hour-square testid from the
          // picker's own dataTestId by appending "-hour-<from>" directly -
          // this must stay in sync with that suffix.
          dateHour: (name: string, from: string) =>
            `${createIDedSelector(name, "e2e_entries-manual-creation-field-")}-hour-${from}`,
        },
        submitButton: "e2e_entries-manual-creation-submit-button",
      },
      modal: {
        wrapper: "e2e_entries-create_edit-modal",
      },
    },
    details: {
      field: (field: string) =>
        createIDedSelector(field, "e2e_entry-details-field-"),
      contactAction: (kind: string) =>
        createIDedSelector(kind, "e2e_entry-details-contact-action-"),
      whatsappChat: "e2e_entry-details-whatsapp-chat",
      whatsappChatLastMessage: "e2e_entry-details-whatsapp-chat-last-message",
      cancelBooking: {
        button: (field: string) =>
          createIDedSelector(field, "e2e_entry-details-cancel-booking-"),
        modalConfirm: "e2e_entry-details-cancel-booking-confirm-button",
      },
    },
    table: {
      row: (id: string) => createIDedSelector(String(id), "e2e_entry-row-"),
      actionsCell: (id: string) =>
        createIDedSelector(String(id), "e2e_entry-actions-cell-"),
      addColumn: "e2e_entries-table-add-column-button",
      tagsCell: (id: string) =>
        createIDedSelector(String(id), "e2e_entry-tags-cell-"),
      bulkHeader: {
        clear: "e2e_entries-bulk-header-clear",
        tags: {
          button: "e2e_entries-bulk-header-tags",
          search: "e2e_entries-bulk-header-tags-search",
          option: (name: string) =>
            createIDedSelector(name, "e2e_entries-bulk-header-tags-option-"),
          add: (name: string) =>
            createIDedSelector(name, "e2e_entries-bulk-header-tags-add-"),
          remove: (name: string) =>
            createIDedSelector(name, "e2e_entries-bulk-header-tags-remove-"),
        },
      },
      hideColumn: (field: string) =>
        createIDedSelector(field, "e2e_entries-table-hide-column-button-"),
      renameColumn: (field: string) =>
        createIDedSelector(field, "e2e_entries-table-rename-column-button-"),
      renameColumnInput: (field: string) =>
        createIDedSelector(field, "e2e_entries-table-rename-column-input-"),
      menu: {
        view: (id: string) =>
          createIDedSelector(String(id), "e2e_entry-view-button-"),
        edit: {
          button: (id: string) =>
            createIDedSelector(String(id), "e2e_entry-edit-button-"),
          drawer: {
            button: "e2e_entry-edit-drawer-button",
            field: (name: string) =>
              createIDedSelector(name, "e2e_entry-edit-drawer-field-"),
          },
        },
        convert: {
          modal: {
            dropdown: {
              open: "e2e_entry-convert-modal-dropdown-open",
              option: (id: string) =>
                createIDedSelector(
                  String(id),
                  "e2e_entry-convert-modal-dropdown-option-",
                ),
            },
            button: "e2e_entry-convert-button",
          },
          button: (id: string) =>
            createIDedSelector(String(id), "e2e_entry-convert-button-"),
        },
        deal: {
          button: (id: string) =>
            createIDedSelector(String(id), "e2e_entry-deal-button-"),
          drawer: {
            button: "e2e_entry-deal-drawer-button",
            name: "e2e_entry-deal-drawer-name-input",
            selectContact: {
              input: "e2e_entry-deal-drawer-select-contact-input",
              option: (id: string) =>
                createIDedSelector(
                  String(id),
                  "e2e_entry-deal-drawer-select-contact-option-",
                ),
            },
            kanban: {
              open: "e2e_entry-deal-drawer-kanban-open",
              option: (id: string) =>
                createIDedSelector(
                  String(id),
                  "e2e_entry-deal-drawer-kanban-option-",
                ),
            },
            stage: {
              open: "e2e_entry-deal-drawer-stage-open",
              option: (id: string) =>
                createIDedSelector(
                  String(id),
                  "e2e_entry-deal-drawer-stage-option-",
                ),
            },
            amount: "e2e_entry-deal-drawer-amount-input",
            closingDate: "e2e_entry-deal-drawer-closing-date-input",
            note: "e2e_entry-deal-drawer-note-input",
          },
        },
        delete: {
          button: (id: string) =>
            createIDedSelector(String(id), "e2e_entry-delete-button-"),
          modal: {
            button: "e2e_entry-delete-confirm-button",
            switch: "e2e_entry-delete-switch",
          },
        },
        restore: {
          button: (id: string) =>
            createIDedSelector(String(id), "e2e_entry-restore-button-"),
        },
        tags: {
          button: (id: string) =>
            createIDedSelector(String(id), "e2e_entry-tags-button-"),
          search: (id: string) =>
            createIDedSelector(String(id), "e2e_entry-tags-search-"),
          option: (tagName: string) =>
            createIDedSelector(String(tagName), "e2e_entry-tags-option-"),
          manage: "e2e_entry-tags-manage",
        },
        assign: {
          button: (id: string) =>
            createIDedSelector(String(id), "e2e_entry-assign-button-"),
          modal: {
            selectMember: {
              input: "e2e_entry-assign-modal-select-member-input",
              option: (id: string) =>
                createIDedSelector(
                  String(id),
                  "e2e_entry-assign-modal-select-member-option-",
                ),
            },
            button: "e2e_entry-assign-modal-confirm-button",
          },
        },
        taskContact: {
          button: (id: string) =>
            createIDedSelector(String(id), "e2e_entry-task-contact-button-"),
        },
        whatsapp: {
          button: (id: string) =>
            createIDedSelector(String(id), "e2e_entry-whatsapp-chat-button-"),
        },
      },
    },
    import: {
      startButton: "e2e_entries-import-start-button",
      conflicts: {
        standardSelect: (index: number) =>
          createIDedSelector(
            index,
            "e2e_entries-import-conflicts-standard-select-",
          ),
        standardOption: (field: string) =>
          createIDedSelector(
            field,
            "e2e_entries-import-conflicts-standard-option-",
          ),
        continueButton: "e2e_entries-import-conflicts-continue-button",
      },
    },
    tableHeader: {
      tagsFilter: {
        open: "e2e_entries-table-header-tags-filter-dropdown",
        option: (tagName: string) =>
          createIDedSelector(
            String(tagName),
            "e2e_entries-table-header-tags-filter-option-",
          ),
      },
      assigneeFilter: {
        open: "e2e_entries-table-header-assignee-filter-dropdown",
        option: (value: string) =>
          createIDedSelector(
            String(value),
            "e2e_entries-table-header-assignee-filter-option-",
          ),
      },
      formsFilter: {
        open: "e2e_entries-table-header-forms-filter-dropdown",
        option: (formId: string) =>
          createIDedSelector(
            String(formId),
            "e2e_entries-table-header-forms-filter-option-",
          ),
      },
      kindFilter: {
        open: "e2e_entries-table-header-kind-filter-dropdown",
        option: (kind: string) =>
          createIDedSelector(
            String(kind),
            "e2e_entries-table-header-kind-filter-option-",
          ),
      },
      searchFilter: {
        input: "e2e_entries-table-header-search-input",
      },
      scopeFilter: {
        open: "e2e_entries-table-header-scope-filter",
      },
    },
    duplicates: {
      picker: {
        card: (key: string) =>
          createIDedSelector(key, "e2e_entries-duplicates-picker-card-"),
        count: (key: string) =>
          createIDedSelector(key, "e2e_entries-duplicates-picker-count-"),
        explore: (key: string) =>
          createIDedSelector(key, "e2e_entries-duplicates-picker-explore-"),
      },
      resolve: {
        otherCard: (entryId: string) =>
          createIDedSelector(entryId, "e2e_entries-duplicates-other-card-"),
        fieldAction: (
          entryId: string,
          field: string,
          action: "use" | "replace" | "append",
        ) =>
          createIDedSelector(
            `${entryId}-${field}-${action}`,
            "e2e_entries-duplicates-field-action-",
          ),
        keptCard: "e2e_entries-duplicates-kept-card",
        merge: "e2e_entries-duplicates-merge-button",
        ignore: "e2e_entries-duplicates-ignore-button",
        preview: (entryId: string) =>
          createIDedSelector(entryId, "e2e_entries-duplicates-preview-button-"),
        previewDrawer: "e2e_entries-duplicates-preview-drawer",
      },
    },
  },
  dashboard: {
    mode: (mode: string) =>
      createIDedSelector(String(mode), "e2e_dashboard-mode-"),
    setup: {
      wrapper: "e2e_dashboard-setup",
      card: (step: string) =>
        createIDedSelector(String(step), "e2e_dashboard-setup-card-"),
      cta: (step: string) =>
        createIDedSelector(String(step), "e2e_dashboard-setup-cta-"),
      appCard: "e2e_dashboard-setup-app-card",
      switchToToday: "e2e_dashboard-setup-switch-to-today",
    },
    tasksDue: "e2e_dashboard-tasks-due",
    today: {
      wrapper: "e2e_dashboard-today",
      kpis: "e2e_dashboard-today-kpis",
      attention: "e2e_dashboard-today-attention",
      sources: "e2e_dashboard-today-sources",
      markDone: (taskId: string) =>
        createIDedSelector(taskId, "e2e_dashboard-today-mark-done-"),
    },
    widgets: {
      [WidgetKind.CLOSED_ENTITIES]: {
        wrapper: "e2e_dashboard-widget-closed-entities",
        cta: "e2e_dashboard-widget-closed-entities-cta",
      },
      [WidgetKind.LAST_ENTRIES]: {
        wrapper: "e2e_dashboard-widget-last-entries",
        cta: "e2e_dashboard-widget-last-entries-cta",
      },
    },
  },
  fahmy: {
    stickyWrapper: "e2e_fahmy-sticky-wrapper",
    tooltip: "e2e_fahmy-tooltip",
    image: (mode: string) =>
      createIDedSelector(String(mode), "e2e_fahmy-content-chat-image-"),
    content: {
      wrapper: "e2e_fahmy-content-wrapper",
      backButton: "e2e_fahmy-content-back-button",
      email: {
        button: "e2e_fahmy-content-email-button",
        wrapper: "e2e_fahmy-content-email-wrapper",
      },
      chat: {
        button: "e2e_fahmy-content-chat-button",
        wrapper: "e2e_fahmy-content-chat-wrapper",
      },
    },
  },
  permissions: {
    teamRole: {
      memberBadge: (memberId: string) =>
        createIDedSelector(String(memberId), "e2e_team-role-member-badge-"),
      memberCustomized: (memberId: string) =>
        createIDedSelector(
          String(memberId),
          "e2e_team-role-member-customized-",
        ),
      memberSelect: (memberId: string) =>
        createIDedSelector(String(memberId), "e2e_team-role-member-select-"),
      option: (role: string) =>
        createIDedSelector(String(role), "e2e_team-role-option-"),
      changeConfirm: "e2e_team-role-change-confirm",
      inviteOpen: "e2e_team-role-invite-open",
      inviteHeaderOpen: "e2e_team-role-invite-header-open",
      inviteAdd: "e2e_team-role-invite-add",
      inviteSubmit: "e2e_team-role-invite-submit",
      inviteSelect: "e2e_team-role-invite-select",
      ownBadge: "e2e_team-role-own-badge",
      ownerRow: "e2e_team-role-owner-row",
      ownerBadge: "e2e_team-role-owner-badge",
      inviteRowError: (index: number) =>
        createIDedSelector(String(index), "e2e_team-role-invite-row-error-"),
    },
    entityAssignModal: {
      selectMember: (id: string) =>
        createIDedSelector(
          String(id),
          "e2e_entity-assign-modal-select-member-option-",
        ),
      button: "e2e_entity-assign-modal-confirm-button",
    },
    entityAssignCheckbox: {
      option: (id: string) =>
        createIDedSelector(String(id), "e2e_entity-assign-checkbox-option-"),
    },
  },
  kanbans: {
    subHeader: {
      menu: {
        dropdown: {
          open: "e2e_deal-subheader-dropdown-open",
          option: (option: string) =>
            createIDedSelector(
              String(option),
              "e2e_deal-subheader-dropdown-option-",
            ),
        },
      },
      selectKanban: {
        open: "e2e_kanban-subheader-select-kanban-open",
        option: (id: string) =>
          createIDedSelector(
            String(id),
            "e2e_kanban-subheader-select-kanban-option-",
          ),
      },
    },
    column: {
      button: (id: string) =>
        createIDedSelector(String(id), "e2e_kanban-column-create-deal-button-"),
    },
    card: {
      wrapper: (id: string) =>
        createIDedSelector(String(id), "e2e_kanban-card-wrapper-"),
      delete: {
        button: (id: string) =>
          createIDedSelector(String(id), "e2e_kanban-card-delete-button-"),
        modalConfirm: "e2e_kanban-card-delete-modal-confirm",
      },
      edit: {
        button: (id: string) =>
          createIDedSelector(String(id), "e2e_kanban-card-edit-button-"),
        drawer: {
          button: "e2e_kanban-card-update-drawer-button",
          name: "e2e_kanban-card-update-drawer-name-input",
          selectContact: {
            input: "e2e_kanban-card-update-drawer-select-contact-input",
            option: (id: string) =>
              createIDedSelector(
                String(id),
                "e2e_kanban-card-update-drawer-select-contact-option-",
              ),
          },
          stage: {
            open: "e2e_kanban-card-update-drawer-stage-open",
            option: (id: string) =>
              createIDedSelector(
                String(id),
                "e2e_kanban-card-update-drawer-stage-option-",
              ),
          },
          amount: "e2e_kanban-card-update-drawer-amount-input",
          closingDate: "e2e_kanban-card-update-drawer-closing-date-input",
          note: "e2e_kanban-card-update-drawer-note-input",
          customFields: {
            button: "e2e_kanban-card-update-drawer-custom-fields-button",
            field: {
              label: (name: string) =>
                createIDedSelector(
                  name,
                  "e2e_kanban-card-update-drawer-custom-field-label-",
                ),
              value: (name: string) =>
                createIDedSelector(
                  name,
                  "e2e_kanban-card-update-drawer-custom-field-value-",
                ),
            },
          },
        },
      },
    },
    settings: {
      save: "e2e_kanban-settings-drawer-save",
      closingReasons: {
        add: (stageType: string) =>
          createIDedSelector(stageType, "e2e_kanban-closing-reason-add-"),
        input: (stageType: string, index: number) =>
          createIDedSelector(
            `${stageType}-${index}`,
            "e2e_kanban-closing-reason-input-",
          ),
        delete: (stageType: string, index: number) =>
          createIDedSelector(
            `${stageType}-${index}`,
            "e2e_kanban-closing-reason-delete-",
          ),
      },
    },
    createEdit: {
      column: {
        button: (id: string) =>
          createIDedSelector(String(id), "e2e_deal-column-create-deal-button-"),
      },
      kanban: {
        name: "e2e_deal-kanban-create-deal-name-input",
        defaultColumns: "e2e_deal-kanban-create-deal-default-columns-switch",
        template: (id: string) =>
          createIDedSelector(
            String(id),
            "e2e_deal-kanban-create-deal-template-option-",
          ),
        button: "e2e_deal-kanban-create-deal-button-create",
      },
    },
    actions: {
      deleteModalConfirm: "e2e_deal-modal-actions-delete-confirm-button",
    },
  },
  modules: {
    settings: {
      button: "e2e_module-settings-button",
      module: {
        edit: (name: string) =>
          createIDedSelector(String(name), "e2e_module-edit-button-"),
        permissions: (name: string) =>
          createIDedSelector(String(name), "e2e_module-permissions-button-"),
      },
    },
    permissions: {
      switch: (memberId: string, action: string) =>
        createIDedSelector(
          `${memberId}-${action}`,
          "e2e_module-permissions-switch-",
        ),
      save: "e2e_module-permissions-save-button",
    },
    createEdit: {
      stepKind: {
        kind: (kind: string) =>
          createIDedSelector(
            String(kind),
            "e2e_module-create-edit-kind-option-",
          ),
      },
      stepBasicInfo: {
        name: {
          input: "e2e_module-create-edit-name-input",
        },
        description: {
          input: "e2e_module-create-edit-description-input",
        },
        icon: {
          button: "e2e_module-create-edit-icon-button",
        },
      },
      stepTemplate: {
        template: (template: string) =>
          createIDedSelector(
            String(template),
            "e2e_module-create-edit-template-option-",
          ),
      },
      next: "e2e_module-create-edit-next-button",
      previous: "e2e_module-create-edit-previous-button",
    },
  },
  roadmap: {
    subHeader: {
      create: "e2e_roadmap-create-button",
    },
    empty: {
      create: "e2e_roadmap-empty-create-button",
    },
    card: {
      wrapper: (id: string) =>
        createIDedSelector(String(id), "e2e_roadmap-item-"),
      edit: (id: string) =>
        createIDedSelector(String(id), "e2e_roadmap-item-edit-"),
      delete: (id: string) =>
        createIDedSelector(String(id), "e2e_roadmap-item-delete-"),
      deleteConfirm: "e2e_roadmap-item-delete-confirm-button",
    },
    createEdit: {
      lang: (lang: string) =>
        createIDedSelector(String(lang), "e2e_roadmap-create-edit-lang-tab-"),
      title: (lang: string) =>
        createIDedSelector(
          String(lang),
          "e2e_roadmap-create-edit-title-input-",
        ),
      descriptions: (lang: string) =>
        createIDedSelector(
          String(lang),
          "e2e_roadmap-create-edit-descriptions-input-",
        ),
      status: {
        open: "e2e_roadmap-create-edit-status-open",
        option: (status: string) =>
          createIDedSelector(
            String(status),
            "e2e_roadmap-create-edit-status-option-",
          ),
      },
      period: "e2e_roadmap-create-edit-period-input",
      order: "e2e_roadmap-create-edit-order-input",
      icon: {
        button: "e2e_roadmap-create-edit-icon-button",
        remove: "e2e_roadmap-create-edit-icon-remove-button",
      },
      submit: "e2e_roadmap-create-edit-submit-button",
      cancel: "e2e_roadmap-create-edit-cancel-button",
    },
  },
};
